import type Phaser from 'phaser';

import type { RegionDefinition } from '@/domain/world/region-definition';
import type { LayeredRegionManifest, RegionRendererHandle } from './types';

export function buildLayeredRegionRenderer(
  scene: Phaser.Scene,
  region: RegionDefinition,
  manifest: LayeredRegionManifest,
): RegionRendererHandle {
  const images: Phaser.GameObjects.Image[] = [];
  const scaleX = region.bounds.w / manifest.width;
  const scaleY = region.bounds.h / manifest.height;
  const add = (
    src: string,
    x: number,
    y: number,
    depth: number,
    originX = 0,
    originY = 0,
  ): Phaser.GameObjects.Image => {
    const image = scene.add
      .image(region.bounds.x + x, region.bounds.y + y, src)
      .setOrigin(originX, originY)
      .setDepth(depth);
    images.push(image);
    return image;
  };

  add(manifest.base, 0, 0, -100).setDisplaySize(region.bounds.w, region.bounds.h);
  for (const layer of manifest.belowActors) {
    add(layer.src, layer.x * scaleX, layer.y * scaleY, layer.depth).setScale(scaleX, scaleY);
  }
  for (const prop of manifest.ySortedProps) {
    const image = add(
      prop.src,
      prop.x * scaleX,
      prop.y * scaleY,
      region.bounds.y + prop.y * scaleY,
      prop.anchorX,
      prop.anchorY,
    );
    if (prop.width !== undefined && prop.height !== undefined) {
      image.setDisplaySize(prop.width * scaleX, prop.height * scaleY);
    } else {
      image.setScale(scaleX, scaleY);
    }
  }
  for (const layer of manifest.aboveActors) {
    add(layer.src, layer.x * scaleX, layer.y * scaleY, layer.depth).setScale(scaleX, scaleY);
  }

  return {
    destroy(): void {
      images.forEach((image) => image.destroy());
    },
  };
}
